import { z } from 'zod';
import { v5 as uuidv5 } from 'uuid';
import { defineCollection, type RecordOf } from '../collection.ts';
import { id, isoDate, isoDateTime, longText, requiredText, shortText, timeOfDay } from '../fields.ts';
import { recurrenceSchema } from '../recurrence.ts';
import { isAdultRole } from '../records.ts';

export const tasks = defineCollection({
  name: 'tasks',
  module: 'tasks',
  label: { one: 'chore', other: 'chores' },
  schema: z.object({
    title: requiredText(200),
    description: longText(5_000).default(''),
    assigneeId: id.nullable().default(null),
    dueDate: isoDate.nullable().default(null),
    dueTime: timeOfDay.nullable().default(null),
    recurrence: recurrenceSchema.nullable().default(null),
    /** Minutes before the due time (or 9:00 on the due date when no time is set). */
    reminderMinutes: z.number().int().min(0).max(20_160).nullable().default(null),
    kind: z.enum(['chore', 'todo']).default('chore'),
    archived: z.boolean().default(false),
  }),
  defaultAudience: 'family',
  childAccess: 'read',
  editPolicy: 'collaborative',
  immutableFields: [],
  manualMergeFields: ['description'],
});

/**
 * One completion per task occurrence. The id is derived from (task,
 * occurrence), so two people ticking the same chore while offline converge on
 * a single completion instead of creating duplicates.
 */
export const taskCompletions = defineCollection({
  name: 'task_completions',
  module: 'tasks',
  label: { one: 'completion', other: 'completions' },
  schema: z.object({
    taskId: id,
    /** "once" for one-off tasks, otherwise the occurrence date (YYYY-MM-DD). */
    occurrence: z.union([z.literal('once'), isoDate]),
    completedBy: id,
    completedAt: isoDateTime,
    note: shortText(500).default(''),
  }),
  parent: { kind: 'fixed', collection: 'tasks', field: 'taskId' },
  defaultAudience: 'family',
  childAccess: 'contribute',
  editPolicy: 'collaborative',
  immutableFields: ['taskId', 'occurrence', 'completedBy'],
  manualMergeFields: [],
  actorFields: ['completedBy'],
  canCreate: ({ actor, parent }) => {
    if (!parent) return false;
    if (isAdultRole(actor.role)) return true;
    const assignee = parent['assigneeId'];
    return assignee === null || assignee === undefined || assignee === actor.userId;
  },
});

const COMPLETION_NAMESPACE = '6f1f0d3e-55a4-4d0e-9a54-3f3c2a1b9e10';

export function completionId(taskId: string, occurrence: string): string {
  return uuidv5(`${taskId}:${occurrence}`, COMPLETION_NAMESPACE);
}

export type Task = RecordOf<typeof tasks>;
export type TaskCompletion = RecordOf<typeof taskCompletions>;
